import { useState, useEffect, useCallback } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { API } from "@/App";
import axios from "axios";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ArrowLeftRight, FileText, FileSpreadsheet } from "lucide-react";
import { fmtHora } from "./constants";

// 4.2 — Módulo único de Movimentações (material + patrimônio) com filtros e exportação
export default function MovimentacoesLogisticaPage() {
  const [dados, setDados] = useState({ itens: [], tipos: [] });
  const [obras, setObras] = useState([]);
  const [f, setF] = useState({ data_inicio: "", data_fim: "", tipo: "", contrato_id: "", origem: "", q: "", pessoa: "" });

  const carregar = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    axios.get(`${API}/logistica/movimentacoes-v2`, { params }).then(r => setDados(r.data)).catch(() => {});
  }, [f]);
  useEffect(() => { const t = setTimeout(carregar, 350); return () => clearTimeout(t); }, [carregar]);
  useEffect(() => { axios.get(`${API}/cronogramas-lookup/contratos-disponiveis`).then(r => setObras(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const linhas = dados.itens || [];

  const exportarExcel = () => {
    const ws = XLSX.utils.json_to_sheet(linhas.map(m => ({
      "Data/hora": fmtHora(m.quando), Tipo: m.tipo, Documento: m.documento || (m.antigo ? "antigo" : ""),
      "Código": m.codigo, Item: m.item, "Qtd.": m.quantidade, "Origem → Destino": m.origem_destino,
      Entregou: m.entregou, Recebeu: m.recebeu, "Situação": m.situacao, Detalhe: m.detalhe })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Movimentações");
    XLSX.writeFile(wb, "Movimentacoes_Logistica.xlsx");
  };

  const exportarPDF = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape", compress: true });
    doc.setFillColor(16, 42, 74).rect(0, 0, 297, 13, "F");
    doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(11);
    doc.text("Movimentações — Logística (material + patrimônio)", 10, 8.5);
    autoTable(doc, { startY: 17, margin: { left: 8, right: 8 },
      head: [["Data/hora", "Tipo", "Doc.", "Item", "Qtd.", "Origem → Destino", "Entregou", "Recebeu", "Situação"]],
      body: linhas.map(m => [fmtHora(m.quando), m.tipo + (m.antigo ? " (antigo)" : ""), m.documento || "—",
        `${m.codigo || ""} ${m.item || ""}`, m.quantidade, m.origem_destino || "—", m.entregou || "—", m.recebeu || "—", m.situacao || "—"]),
      styles: { fontSize: 6.3, cellPadding: 1 }, headStyles: { fillColor: [16, 42, 74], fontSize: 6.3 } });
    doc.save("Movimentacoes_Logistica.pdf");
  };

  const docLink = (m) => {
    if (m.romaneio_id) return `/logistica/romaneios/${m.romaneio_id}`;
    if (m.cautela_id) return `/logistica/cautelas?abrir=${m.cautela_id}`;
    return null;
  };

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="movimentacoes-logistica-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-luvcorp-navy flex items-center gap-2">
              <ArrowLeftRight className="h-6 w-6 text-luvcorp-gold" /> Movimentações
            </h1>
            <p className="text-sm text-slate-500">Material e patrimônio em uma só visão. Lançamentos antigos aparecem somente leitura, marcados.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportarPDF} data-testid="btn-export-pdf"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={exportarExcel} data-testid="btn-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            <Input type="date" value={f.data_inicio} onChange={e => set("data_inicio", e.target.value)} data-testid="filtro-inicio" />
            <Input type="date" value={f.data_fim} onChange={e => set("data_fim", e.target.value)} data-testid="filtro-fim" />
            <NativeSelect value={f.tipo} onValueChange={v => set("tipo", v)} placeholder="Tipo" data-testid="filtro-tipo"
              options={[{ value: "", label: "Todos os tipos" }, ...(dados.tipos || []).map(t => ({ value: t, label: t }))]} />
            <NativeSelect value={f.contrato_id} onValueChange={v => set("contrato_id", v)} placeholder="Obra" data-testid="filtro-obra" searchable
              options={[{ value: "", label: "Todas as obras" }, ...obras.map(o => ({ value: o.contrato_id, label: o.titulo }))]} />
            <NativeSelect value={f.origem} onValueChange={v => set("origem", v)} placeholder="Origem" data-testid="filtro-origem"
              options={[{ value: "", label: "Material + Patrimônio" }, { value: "material", label: "Só material" }, { value: "patrimonio", label: "Só patrimônio" }]} />
            <Input placeholder="Item / código" value={f.q} onChange={e => set("q", e.target.value)} data-testid="filtro-item" />
            <Input placeholder="Pessoa" value={f.pessoa} onChange={e => set("pessoa", e.target.value)} data-testid="filtro-pessoa" />
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-luvcorp-navy text-white text-left">
                  {["Data/hora", "Tipo", "Documento", "Item", "Qtd.", "Origem → Destino", "Entregou", "Recebeu", "Situação"].map(h =>
                    <th key={h} className="px-2.5 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {linhas.length === 0 ? (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Sem movimentações com estes filtros.</td></tr>
                ) : linhas.map((m, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${m.antigo ? "text-slate-400" : ""}`} data-testid={`mov-row-${i}`}>
                    <td className="px-2.5 py-2 whitespace-nowrap">{fmtHora(m.quando)}</td>
                    <td className="px-2.5 py-2">
                      <Badge variant="outline" className={m.origem === "material" ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-amber-50 text-amber-700 border-amber-300"}>{m.tipo}</Badge>
                      {m.antigo && <span className="block text-[10px] text-slate-400">lançamento antigo</span>}
                    </td>
                    <td className="px-2.5 py-2">
                      {docLink(m) ? <a href={docLink(m)} className="text-luvcorp-navy font-semibold underline" data-testid={`doc-link-${i}`}>{m.documento}</a> : (m.documento || "—")}
                    </td>
                    <td className="px-2.5 py-2">{m.codigo} {m.item}</td>
                    <td className="px-2.5 py-2">{m.quantidade}</td>
                    <td className="px-2.5 py-2 max-w-56">{m.origem_destino || "—"}</td>
                    <td className="px-2.5 py-2">{m.entregou || "—"}</td>
                    <td className="px-2.5 py-2">{m.recebeu || "—"}</td>
                    <td className="px-2.5 py-2 capitalize">{m.situacao || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
